import {
	differenceInDays,
	differenceInMonths,
	differenceInWeeks,
	differenceInYears
} from "date-fns";
import {
	DateField,
	DecimalField,
	Field,
	FieldType,
	GroupField,
	IntegerField,
	SelectField,
	TextField
} from "../FieldService";

export const ArgumentType = {
	TEXT: "TEXT",
	NUMBER: "NUMBER",
	EXPRESSION: "EXPRESSION",
	FUNCTION: "FUNCTION"
} as const;

export abstract class Argument {
	abstract toString(): string;
}

export class Textual extends Argument {
	private readonly value: string;

	constructor(value: string) {
		super();

		this.value = value;
	}

	public toString(): string {
		return this.value;
	}
}

export class Numeric extends Argument {
	private readonly value: number;

	constructor(value: number) {
		super();

		this.value = value;
	}

	public toString(): string {
		return this.value.toString();
	}
}

export class Expression extends Argument {
	private readonly value: string;

	constructor(value: string) {
		super();

		this.value = value;
	}

	public resolve(infoFields: Array<Field>): Argument {
		if (!this.value.startsWith("$information.")) {
			throw new Error(
				"Unsupported expression: Should start with $information"
			);
		}

		const root = new GroupField("");
		root.children = infoFields;

		const fieldNames = this.value.substring(13).split(".");

		let field: Field = root;
		for (const fieldName of fieldNames) {
			if (field.type !== FieldType.GROUP) {
				throw new Error(`${fieldName} is not a valid child field.`);
			}

			field = this.getField((field as GroupField).children, fieldName);
		}

		switch (field.type) {
			case FieldType.TEXT:
				return new Textual((field as TextField).value);
			case FieldType.INTEGER:
				return new Numeric((field as IntegerField).value);
			case FieldType.DECIMAL:
				return new Numeric((field as DecimalField).value);
			case FieldType.DATE:
				return new Textual((field as DateField).value);
			case FieldType.SELECT:
				return new Textual((field as SelectField).value);
			default:
				throw new Error(`Unsupported field type: ${field.type}`);
		}
	}

	public toString(): string {
		return this.value;
	}

	private getField(fields: Array<Field>, fieldName: string): Field {
		const matchingFields = fields.filter(field => field.name === fieldName);

		if (matchingFields.length !== 1) {
			throw new Error(
				`Found ${matchingFields.length} fields with name ${fieldName}`
			);
		}

		return matchingFields[0];
	}
}

export class Functional extends Argument {
	private readonly name: string;
	private readonly args: Array<Argument>;

	constructor(name: string, args: Array<Argument>) {
		super();

		this.name = name;
		this.args = args;
	}

	public execute(infoFields: Array<Field>): Argument {
		switch (this.name) {
			case "NOW":
				return new Textual(new Date().toISOString());
			case "DATEDIF":
				const [start, end, unit] = this.resolveArguments(infoFields);

				switch (unit.toString()) {
					case "Y":
						return new Numeric(
							differenceInYears(end.toString(), start.toString())
						);
					case "M":
						return new Numeric(
							differenceInMonths(end.toString(), start.toString())
						);
					case "W":
						return new Numeric(
							differenceInWeeks(end.toString(), start.toString())
						);
					case "D":
						return new Numeric(
							differenceInDays(end.toString(), start.toString())
						);
					default:
						throw new Error(`Invalid DATEDIF unit: ${unit}`);
				}
			default:
				throw new Error(`Unknown function: ${this.name}`);
		}
	}

	public toString(): string {
		return `${this.name}(${this.args})`;
	}

	private resolveArguments(infoFields: Array<Field>): Array<Argument> {
		return this.args.map(argument => {
			if (argument instanceof Expression) {
				return argument.resolve(infoFields);
			} else if (argument instanceof Functional) {
				return argument.execute(infoFields);
			} else {
				return argument;
			}
		});
	}
}

export function ofValue(value: string): Argument {
	if (value.startsWith("$")) {
		return new Expression(value);
	} else if (!isNaN(Number(value))) {
		return new Numeric(Number(value));
	} else {
		return new Textual(value);
	}
}
